import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import CarnetSantePanel from '../components/CarnetSantePanel';
import GlobalHealthDashboard from '../components/GlobalHealthDashboard';
import {
  Building, School, Church, Dumbbell, MapPin, Plus, ArrowLeft,
  Save, X, Edit, Trash2, AlertTriangle, CheckCircle2, XCircle,
  ChevronRight, Loader2, Zap, Wrench, Eye, ClipboardList,
  ThermometerSun, Lightbulb, Droplets, Wind, PaintBucket,
  Shield, Leaf, Euro, Upload, FileUp, TrendingDown
} from 'lucide-react';

const ETAT_COLORS = {
  'Bon': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300', dot: 'bg-green-500' },
  'Correct': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500' },
  'Dégradé': { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', dot: 'bg-amber-500' },
  'Mauvais': { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', dot: 'bg-orange-500' },
  'Critique': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300', dot: 'bg-red-500' },
  'Non évalué': { bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200', dot: 'bg-slate-300' },
};

const CATEGORY_LABELS = {
  enveloppe: { label: "Enveloppe du bâtiment", icon: Building, color: "blue" },
  energie: { label: "Systèmes énergétiques", icon: Zap, color: "amber" },
  second_oeuvre: { label: "Second oeuvre", icon: PaintBucket, color: "purple" },
  reglementaire: { label: "Réglementaire", icon: Shield, color: "red" },
  exterieur: { label: "Extérieurs", icon: Leaf, color: "green" },
};

export default function SitesCollectivite({ clientId, clientName, onBack }) {
  const [view, setView] = useState('list'); // list, detail, add
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteDetail, setSiteDetail] = useState(null);
  const [refData, setRefData] = useState(null);
  const [editingAudit, setEditingAudit] = useState(null);
  const [saving, setSaving] = useState(false);

  // New site form
  const [siteForm, setSiteForm] = useState({
    nom: '', type_site: 'Ecole', adresse: '', ville: '', code_postal: '',
    surface: '', annee_construction: '', notes: '',
    hauteur_sous_plafond_m: '', type_chauffage: '', energie_chauffage: '',
    puissance_chauffage_kw: '', type_ventilation: '',
    r_isolant_combles: '', secteur_activite: '',
  });

  // Audit form
  const [auditForm, setAuditForm] = useState({
    etat: 'Non évalué', notes: '', travaux_recommandes: '', priorite: 'medium', cout_estime: ''
  });

  // PDF Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    loadSites();
    loadRefData();
  }, [clientId]);

  const loadSites = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/sites/by-client/${clientId}`);
      setSites(res.data.sites || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadRefData = async () => {
    try {
      const res = await api.get('/sites/reference-data');
      setRefData(res.data);
    } catch (e) { console.error(e); }
  };

  const loadSiteDetail = async (siteId) => {
    try {
      const res = await api.get(`/sites/${siteId}`);
      setSiteDetail(res.data);
      setView('detail');
    } catch (e) { console.error(e); }
  };

  const handleCreateSite = async () => {
    setSaving(true);
    try {
      await api.post(`/sites?gestion_client_id=${clientId}`, siteForm);
      setSiteForm({
        nom: '', type_site: 'Ecole', adresse: '', ville: '', code_postal: '',
        surface: '', annee_construction: '', notes: '',
        hauteur_sous_plafond_m: '', type_chauffage: '', energie_chauffage: '',
        puissance_chauffage_kw: '', type_ventilation: '',
        r_isolant_combles: '', secteur_activite: '',
      });
      setView('list');
      loadSites();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleSaveAudit = async (elementId) => {
    setSaving(true);
    try {
      await api.patch(`/sites/${siteDetail.site.site_id}/audit`, {
        element_id: elementId,
        etat: auditForm.etat,
        notes: auditForm.notes,
        travaux_recommandes: auditForm.travaux_recommandes,
        priorite: auditForm.priorite,
        cout_estime: parseFloat(auditForm.cout_estime) || null
      });
      await loadSiteDetail(siteDetail.site.site_id);
      setEditingAudit(null);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Supprimer ce site ?')) return;
    try {
      await api.delete(`/sites/${siteId}`);
      loadSites();
    } catch (e) { console.error(e); }
  };

  // PDF Import
  const handlePdfImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('gestion_client_id', clientId);
      
      const res = await api.post('/sites/import-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      
      setImportResult(res.data);
      loadSites();
      // Auto-open the new site
      if (res.data.site_id) {
        setTimeout(() => loadSiteDetail(res.data.site_id), 500);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setImportResult({ success: false, message: typeof detail === 'string' ? detail : "Erreur d'import" });
    }
    setImporting(false);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ==========================================
  // SITE LIST VIEW
  // ==========================================
  if (view === 'list') {
    return (
      <div key="sites-view-list" className="space-y-6" data-testid="sites-list-view">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onBack} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Sites & Bâtiments</h2>
              <p className="text-slate-500 text-sm">{clientName}</p>
            </div>
          </div>
          <Button onClick={() => setView('add')} className="bg-blue-600 hover:bg-blue-700" data-testid="add-site-btn">
            <Plus className="w-4 h-4 mr-2" /> Ajouter un site
          </Button>
        </div>

        {/* Import PDF Banner — retiré (feature CEE-only) */}
        {false && (
        <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl p-5 border border-violet-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-violet-100 rounded-xl">
              <FileUp className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Importer un audit PDF</h3>
              <p className="text-sm text-slate-500">Uploadez un rapport DPE, audit énergétique ou état des lieux — l'IA crée le site et remplit l'audit automatiquement</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {importing && (
              <span className="flex items-center gap-2 text-sm text-violet-600">
                <Loader2 className="w-4 h-4 animate-spin" /> Analyse IA en cours...
              </span>
            )}
            {importResult && (
              <span className={`text-sm font-medium ${importResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {importResult.message}
              </span>
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePdfImport}
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              data-testid="pdf-import-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="pdf-import-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? 'Analyse...' : 'Importer un PDF'}
            </Button>
          </div>
        </div>
        )}

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" /></div>
        ) : sites.length === 0 ? (
          <div className="text-center py-20 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl border border-blue-100" data-testid="empty-patrimoine">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-white shadow-sm flex items-center justify-center mb-4">
              <Building className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="font-bold text-slate-900 text-xl mb-2">Carnet de Santé du Patrimoine</h3>
            <p className="text-slate-600 text-sm mb-6 max-w-md mx-auto leading-relaxed">
              Commencez par enregistrer un premier bâtiment afin de constituer progressivement le carnet de santé du patrimoine de cette commune.
            </p>
            <Button onClick={() => setView('add')} className="bg-blue-600 hover:bg-blue-700" size="lg">
              <Plus className="w-5 h-5 mr-2" /> Ajouter un bâtiment
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <GlobalHealthDashboard
              sites={sites}
              clientName={clientName}
              onSiteClick={loadSiteDetail}
            />
            <div className="grid gap-4">
              {sites.map(site => (
              <div
                key={site.site_id}
                onClick={() => loadSiteDetail(site.site_id)}
                className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg cursor-pointer transition-all group"
                data-testid={`site-card-${site.site_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Building className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{site.nom}</h3>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-sm text-blue-600 font-medium">{site.type_site}</span>
                        {site.ville && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {site.ville}
                          </span>
                        )}
                        {site.surface && <span className="text-xs text-slate-400">{site.surface} m²</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {(site.score_global !== null && site.score_global !== undefined) ? (
                      <div className="text-center">
                        <p className="text-lg font-bold text-slate-900 tabular-nums">{Math.round(site.score_global)}<span className="text-xs text-slate-400 font-normal">/100</span></p>
                        <p className="text-xs text-slate-500">Santé</p>
                      </div>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">Non évalué</span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteSite(site.site_id); }}
                      className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      data-testid={`delete-site-${site.site_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==========================================
  // ADD SITE VIEW
  // ==========================================
  if (view === 'add') {
    return (
      <div key="sites-view-add" className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => setView('list')} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
          <h2 className="text-xl font-bold text-slate-900">Nouveau site</h2>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom du site *</label>
              <Input value={siteForm.nom} onChange={e => setSiteForm({...siteForm, nom: e.target.value})} placeholder="Ex: École Jean Jaurès" data-testid="site-nom-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type de site *</label>
              <select value={siteForm.type_site} onChange={e => setSiteForm({...siteForm, type_site: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2">
                {refData?.types_sites?.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Adresse</label>
            <Input value={siteForm.adresse} onChange={e => setSiteForm({...siteForm, adresse: e.target.value})} placeholder="12 Rue de la République" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
              <Input value={siteForm.ville} onChange={e => setSiteForm({...siteForm, ville: e.target.value})} placeholder="Méry-sur-Oise" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Code postal</label>
              <Input value={siteForm.code_postal} onChange={e => setSiteForm({...siteForm, code_postal: e.target.value})} placeholder="95540" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Surface (m²)</label>
              <Input type="number" value={siteForm.surface} onChange={e => setSiteForm({...siteForm, surface: e.target.value})} placeholder="850" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Année de construction</label>
              <Input value={siteForm.annee_construction} onChange={e => setSiteForm({...siteForm, annee_construction: e.target.value})} placeholder="1975" />
            </div>
            <div className="md:col-span-2 mt-2 pt-4 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" /> Données techniques CEE (optionnelles, améliorent l'éligibilité)
              </h3>
              <p className="text-xs text-slate-500 mb-3">Renseignez ce que vous connaissez : laissez vide si inconnu, l'audit affichera "à vérifier".</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Hauteur sous plafond (m)</label>
              <Input type="number" step="0.1" value={siteForm.hauteur_sous_plafond_m}
                onChange={e => setSiteForm({...siteForm, hauteur_sous_plafond_m: e.target.value})}
                placeholder="2.50" data-testid="site-hauteur" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type de chauffage</label>
              <select className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                value={siteForm.type_chauffage}
                onChange={e => setSiteForm({...siteForm, type_chauffage: e.target.value})}
                data-testid="site-type-chauffage">
                <option value="">— Non renseigné —</option>
                <option value="Convectif">Convectif (air pulsé)</option>
                <option value="Radiatif">Radiatif (radiateurs/panneaux)</option>
                <option value="PAC air/eau">Pompe à chaleur air/eau</option>
                <option value="PAC air/air">Pompe à chaleur air/air</option>
                <option value="Chaudière gaz">Chaudière gaz condensation</option>
                <option value="Chaudière fioul">Chaudière fioul</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Énergie de chauffage</label>
              <select className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                value={siteForm.energie_chauffage}
                onChange={e => setSiteForm({...siteForm, energie_chauffage: e.target.value})}>
                <option value="">— Non renseigné —</option>
                <option value="Électricité">Électricité</option>
                <option value="Gaz">Gaz naturel</option>
                <option value="Fioul">Fioul</option>
                <option value="Biomasse">Biomasse</option>
                <option value="Réseau de chaleur">Réseau de chaleur</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Puissance chauffage (kW)</label>
              <Input type="number" step="0.1" value={siteForm.puissance_chauffage_kw}
                onChange={e => setSiteForm({...siteForm, puissance_chauffage_kw: e.target.value})}
                placeholder="80" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type de ventilation</label>
              <select className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                value={siteForm.type_ventilation}
                onChange={e => setSiteForm({...siteForm, type_ventilation: e.target.value})}>
                <option value="">— Non renseigné —</option>
                <option value="Débit constant">Débit constant</option>
                <option value="Modulée proportionnelle">Modulée proportionnelle (CO₂/HR)</option>
                <option value="Modulée présence">Modulée présence (détection)</option>
                <option value="Double flux">Double flux avec échangeur</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">R isolant combles (m².K/W)</label>
              <Input type="number" step="0.1" value={siteForm.r_isolant_combles}
                onChange={e => setSiteForm({...siteForm, r_isolant_combles: e.target.value})}
                placeholder="3.5" />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <Input value={siteForm.notes} onChange={e => setSiteForm({...siteForm, notes: e.target.value})} placeholder="Infos complémentaires..." />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setView('list')}>Annuler</Button>
            <Button onClick={handleCreateSite} disabled={!siteForm.nom || saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Créer le site
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // SITE DETAIL VIEW (Audit)
  // ==========================================
  if (view === 'detail' && siteDetail) {
    const { site, audits, summary, rapport, cee_analyse } = siteDetail;
    const equipements = siteDetail.equipements || [];
    const donneesChiffrees = siteDetail.donnees_chiffrees || {};
    const imagesExtraites = siteDetail.images_extraites || [];
    const categories = {};
    audits.forEach(a => {
      if (!categories[a.category]) categories[a.category] = [];
      categories[a.category].push(a);
    });

    return (
      <div key={`sites-view-detail-${site.site_id}`} className="space-y-6" data-testid="site-detail-view">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => { setView('list'); setSiteDetail(null); }} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h2 className="text-xl font-bold text-slate-900">{site.nom}</h2>
              <p className="text-slate-500 text-sm">
                {site.type_site} {site.ville && `- ${site.ville}`} {site.surface && `- ${site.surface} m²`}
                {site.annee_construction && ` - Construit en ${site.annee_construction}`}
                {site.dpe_actuel && <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">DPE {site.dpe_actuel}</span>}
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="text-red-500 border-red-200 hover:bg-red-50"
            onClick={() => { handleDeleteSite(site.site_id); setView('list'); setSiteDetail(null); }}
            data-testid="delete-site-detail-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
        </div>

        {/* Carnet de Santé du Bâtiment — vue principale (remplace l'ancien onglet CEE) */}
        <CarnetSantePanel site={site} />

      </div>
    );
    // Ancien contenu CEE/Audit désactivé — désormais consolidé dans CarnetSantePanel (onglet Mutuelle & aides).
    // Bloc historique conservé pour référence future — non rendu.
    // eslint-disable-next-line no-unreachable
    return (
      <div className="hidden">

        {/* Rapport de synthèse (from PDF import) */}
        {rapport && rapport.etat_general && (
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200" data-testid="rapport-synthese">
            <h3 className="font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-indigo-600" />
              Rapport de synthèse
              {rapport.classification_urgence && (
                <span className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  rapport.classification_urgence === 'Urgente' ? 'bg-red-100 text-red-700' :
                  rapport.classification_urgence === 'Importante' ? 'bg-orange-100 text-orange-700' :
                  'bg-blue-100 text-blue-700'
                }`}>{rapport.classification_urgence}</span>
              )}
            </h3>
            <p className="text-slate-700 mb-4">{rapport.etat_general}</p>
            <div className="grid grid-cols-2 gap-4">
              {rapport.points_critiques?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Points critiques</p>
                  <ul className="space-y-1">
                    {rapport.points_critiques.map((p, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {rapport.recommandations_prioritaires?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-indigo-700 mb-2 flex items-center gap-1"><Wrench className="w-3.5 h-3.5" /> Recommandations prioritaires</p>
                  <ul className="space-y-1">
                    {rapport.recommandations_prioritaires.map((r, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {rapport.estimation_budget_global && (
              <p className="mt-3 text-sm font-medium text-slate-600">
                Budget global estimé : <span className="text-slate-900 text-base">{rapport.estimation_budget_global?.toLocaleString('fr-FR')}€</span>
              </p>
            )}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-5 gap-3">
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">Éléments évalués</p>
            <p className="text-2xl font-bold text-slate-900">{summary.evaluated}<span className="text-sm text-slate-400 font-normal">/{summary.total_elements}</span></p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-red-200">
            <p className="text-xs text-slate-500 mb-1">Points critiques</p>
            <p className="text-2xl font-bold text-red-600">{summary.critiques}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-green-200">
            <p className="text-xs text-slate-500 mb-1">Aides CEE ({summary.zone_climatique})</p>
            <p className="text-2xl font-bold text-green-600">{summary.cee_potentiel}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 border border-green-300">
            <p className="text-xs text-green-600 mb-1">Prime CEE estimée</p>
            <p className="text-2xl font-bold text-green-700">{summary.prime_cee_totale?.toLocaleString('fr-FR') || 0}€</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <p className="text-xs text-slate-500 mb-1">Coût total estimé</p>
            <p className="text-2xl font-bold text-slate-900">{summary.cout_total_estime?.toLocaleString('fr-FR') || 0}€</p>
          </div>
        </div>

        {/* Audit by category */}
        {Object.entries(categories).map(([catId, elements]) => {
          const catInfo = CATEGORY_LABELS[catId] || { label: catId, icon: ClipboardList, color: "slate" };
          const CatIcon = catInfo.icon;
          return (
            <div key={catId} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className={`p-4 border-b border-slate-100 bg-${catInfo.color}-50`}>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <CatIcon className={`w-4 h-4 text-${catInfo.color}-600`} />
                  {catInfo.label}
                </h3>
              </div>
              <div className="divide-y divide-slate-100">
                {elements.map(audit => {
                  const colors = ETAT_COLORS[audit.etat] || ETAT_COLORS['Non évalué'];
                  const isEditing = editingAudit === audit.element_id;

                  if (isEditing) {
                    return (
                      <div key={audit.element_id} className="p-4 bg-blue-50 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-slate-900">{audit.label}</p>
                          <Button variant="ghost" size="sm" onClick={() => setEditingAudit(null)}><X className="w-4 h-4" /></Button>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">État</label>
                            <select value={auditForm.etat} onChange={e => setAuditForm({...auditForm, etat: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
                              {refData?.etats?.map(e => <option key={e} value={e}>{e}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Priorité</label>
                            <select value={auditForm.priorite} onChange={e => setAuditForm({...auditForm, priorite: e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm">
                              <option value="low">Basse</option>
                              <option value="medium">Moyenne</option>
                              <option value="high">Haute</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Coût estimé (€)</label>
                            <Input type="number" value={auditForm.cout_estime} onChange={e => setAuditForm({...auditForm, cout_estime: e.target.value})} className="h-8 text-sm" placeholder="0" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Notes / Constat</label>
                          <Input value={auditForm.notes} onChange={e => setAuditForm({...auditForm, notes: e.target.value})} className="h-8 text-sm" placeholder="Détails de l'état constaté..." />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Travaux recommandés</label>
                          <Input value={auditForm.travaux_recommandes} onChange={e => setAuditForm({...auditForm, travaux_recommandes: e.target.value})} className="h-8 text-sm" placeholder="Travaux à prévoir..." />
                        </div>
                        <div className="flex justify-end">
                          <Button size="sm" onClick={() => handleSaveAudit(audit.element_id)} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Enregistrer
                          </Button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={audit.element_id}
                      onClick={() => {
                        setEditingAudit(audit.element_id);
                        setAuditForm({
                          etat: audit.etat, notes: audit.notes || '',
                          travaux_recommandes: audit.travaux_recommandes || '',
                          priorite: audit.priorite || 'medium',
                          cout_estime: audit.cout_estime || ''
                        });
                      }}
                      className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
                          <p className="font-medium text-slate-900 text-sm">{audit.label}</p>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                            {audit.etat}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          {audit.cee?.eligible && (
                            <div className="flex items-center gap-1 px-2 py-0.5 bg-green-100 rounded-full">
                              <Zap className="w-3 h-3 text-green-600" />
                              <span className="text-xs font-medium text-green-700">{audit.cee.prime_estimee?.toLocaleString('fr-FR')}€</span>
                            </div>
                          )}
                          {audit.cout_estime && (
                            <span className="text-sm font-medium text-slate-600">{audit.cout_estime?.toLocaleString('fr-FR')}€</span>
                          )}
                          <Edit className="w-3.5 h-3.5 text-slate-400" />
                        </div>
                      </div>
                      {/* Extra info row */}
                      {(audit.notes || audit.travaux_recommandes || audit.cee?.eligible || audit.sous_elements?.length > 0) && (
                        <div className="mt-2 ml-5.5 space-y-1.5">
                          {audit.notes && <p className="text-xs text-slate-500">{audit.notes}</p>}
                          {/* Sous-elements by zone */}
                          {audit.sous_elements?.length > 0 && (
                            <div className="space-y-1 mt-1">
                              {audit.sous_elements.map((se, si) => {
                                const seColors = ETAT_COLORS[se.etat] || ETAT_COLORS['Non évalué'];
                                return (
                                  <div key={si} className={`p-2 rounded border-l-2 ${seColors.border} bg-slate-50`}>
                                    <div className="flex items-center gap-2">
                                      <span className={`w-1.5 h-1.5 rounded-full ${seColors.dot}`} />
                                      <span className="text-xs font-semibold text-slate-700">{se.zone}</span>
                                      <span className={`px-1.5 py-0.5 rounded text-xs ${seColors.bg} ${seColors.text}`}>{se.etat}</span>
                                      {se.coefficient_u && <span className="text-xs text-slate-400">U={se.coefficient_u} W/m².K</span>}
                                    </div>
                                    {se.description && <p className="text-xs text-slate-500 mt-0.5 ml-3.5">{se.description}</p>}
                                    {se.problemes && <p className="text-xs text-red-500 mt-0.5 ml-3.5">{se.problemes}</p>}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {audit.travaux_recommandes && (
                            <p className="text-xs text-amber-600 flex items-center gap-1">
                              <Wrench className="w-3 h-3" /> {audit.travaux_recommandes}
                            </p>
                          )}
                          {audit.cee?.eligible && (
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                {audit.cee.eligible_status === 'yes' && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-semibold">
                                    <CheckCircle2 className="w-3 h-3" /> Éligible
                                  </span>
                                )}
                                {audit.cee.eligible_status === 'maybe' && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-semibold">
                                    <AlertTriangle className="w-3 h-3" /> À vérifier
                                  </span>
                                )}
                                {audit.cee.eligible_status === 'no' && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-semibold">
                                    Non éligible
                                  </span>
                                )}
                                <p className="text-xs text-green-600 flex items-center gap-1">
                                  <Zap className="w-3 h-3" /> {audit.cee.code} — {audit.cee.label}
                                </p>
                                <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded font-medium">
                                  {audit.cee.kwh_cumac?.toLocaleString('fr-FR')} kWh cumac
                                </span>
                                {audit.cee.prime_estimee > 0 && (
                                  <span className="text-xs bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-bold">
                                    ≈ {audit.cee.prime_estimee.toLocaleString('fr-FR')} €
                                  </span>
                                )}
                              </div>
                              {audit.cee.prerequisites_passed?.length > 0 && (
                                <details className="text-xs">
                                  <summary className="cursor-pointer text-emerald-700 hover:text-emerald-900 font-medium">
                                    {audit.cee.prerequisites_passed.length} pré-requis vérifié{audit.cee.prerequisites_passed.length > 1 ? 's' : ''} ✓
                                  </summary>
                                  <ul className="mt-1 ml-4 space-y-0.5 text-slate-600">
                                    {audit.cee.prerequisites_passed.map((p) => (
                                      <li key={p.id} className="flex items-start gap-1">
                                        <span className="text-emerald-500">✓</span>
                                        <span><span className="font-medium">{p.label}</span>{p.value && <span className="text-slate-500"> · {p.value}</span>}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </details>
                              )}
                              {audit.cee.prerequisites_manual?.length > 0 && (
                                <details className="text-xs">
                                  <summary className="cursor-pointer text-amber-700 hover:text-amber-900 font-medium">
                                    {audit.cee.prerequisites_manual.length} pré-requis à vérifier manuellement
                                  </summary>
                                  <ul className="mt-1 ml-4 space-y-0.5 text-slate-600">
                                    {audit.cee.prerequisites_manual.map((p) => (
                                      <li key={p.id} className="flex items-start gap-1">
                                        <span className="text-amber-500">•</span>
                                        <span>{p.label}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </details>
                              )}
                              {audit.cee.prerequisites_failed?.length > 0 && (
                                <div className="text-xs text-red-700 ml-1">
                                  ⛔ {audit.cee.prerequisites_failed.map((p) => p.label).join(' · ')}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* CEE Analysis Section */}
        {cee_analyse && (cee_analyse.cumulable?.length > 0 || cee_analyse.conflicts?.length > 0) && (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200" data-testid="cee-analyse-section">
            <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-green-600" />
              Analyse des aides CEE
            </h3>
            
            {/* Cumulable operations */}
            {cee_analyse.cumulable?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" /> Aides cumulables ({cee_analyse.cumulable.length})
                </p>
                <div className="space-y-2">
                  {cee_analyse.cumulable.map((op, i) => (
                    <div key={i} className="p-3 bg-white rounded-lg border border-green-200 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{op.code} — {op.label}</p>
                        <p className="text-xs text-slate-500">{op.kwh_cumac?.toLocaleString('fr-FR')} kWh cumac · Zone {op.zone_climatique}</p>
                      </div>
                      <p className="font-bold text-green-700">{op.prime_estimee?.toLocaleString('fr-FR')}€</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-3 bg-green-100 rounded-lg">
                  <p className="font-semibold text-green-800">
                    Total primes CEE cumulables : {cee_analyse.total_prime_cumulable?.toLocaleString('fr-FR')}€
                  </p>
                </div>
              </div>
            )}
            
            {/* Conflicts */}
            {cee_analyse.conflicts?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> Attention : aides non cumulables entre elles
                </p>
                {cee_analyse.conflicts.map((c, i) => (
                  <div key={i} className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-2">
                    <p className="text-sm font-medium text-amber-800">{c.raison}</p>
                    <p className="text-xs text-amber-600 mt-1">{c.conseil}</p>
                  </div>
                ))}
              </div>
            )}
            
            {cee_analyse.note && (
              <p className="text-xs text-slate-500 italic mt-2">{cee_analyse.note}</p>
            )}
          </div>
        )}

        {/* Travaux proposés */}
        {siteDetail.travaux_proposes?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 bg-amber-50">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-amber-600" />
                Travaux recommandés ({siteDetail.travaux_proposes.length})
              </h3>
            </div>
            <div className="divide-y divide-slate-100">
              {siteDetail.travaux_proposes.map((t, i) => (
                <div key={i} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-slate-200 rounded text-xs font-mono">{t.numero || `Action ${i+1}`}</span>
                      <p className="font-medium text-slate-900 text-sm">{t.titre}</p>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      {t.cout_estime && <span className="text-slate-600">{t.cout_estime?.toLocaleString('fr-FR')}€</span>}
                      {t.temps_retour && <span className="text-green-600">ROI: {t.temps_retour}</span>}
                    </div>
                  </div>
                  {t.description && <p className="text-xs text-slate-500 mt-1 ml-14">{t.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scénarios */}
        {siteDetail.scenarios?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 bg-blue-50">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-blue-600" />
                Scénarios de rénovation
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5">
              {siteDetail.scenarios.map((s, i) => (
                <div key={i} className={`p-4 rounded-xl border-2 ${i === 0 ? 'border-green-300 bg-green-50' : i === 1 ? 'border-blue-300 bg-blue-50' : 'border-purple-300 bg-purple-50'}`}>
                  <h4 className="font-bold text-slate-900 mb-2">{s.nom}</h4>
                  <p className="text-sm text-slate-600 mb-3">{s.objectif}</p>
                  {s.investissement && <p className="text-sm"><span className="text-slate-500">Investissement:</span> <span className="font-semibold">{s.investissement?.toLocaleString('fr-FR')}€</span></p>}
                  {s.economie_30ans && <p className="text-sm"><span className="text-slate-500">Économie 30 ans:</span> <span className="font-semibold text-green-700">{s.economie_30ans?.toLocaleString('fr-FR')}€</span></p>}
                  {s.dpe_apres && <p className="text-sm mt-1"><span className="text-slate-500">DPE après:</span> <span className="font-bold text-lg">{s.dpe_apres}</span></p>}
                  {s.actions_incluses?.length > 0 && (
                    <p className="text-xs text-slate-500 mt-2">Actions: {s.actions_incluses.join(', ')}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Informations complémentaires */}
        {site.informations_complementaires && Object.values(site.informations_complementaires).some(v => v && v !== "null") && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4 text-slate-500" />
              Diagnostics complémentaires
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(site.informations_complementaires).map(([key, val]) => {
                if (!val || val === "null" || val === "Non mentionné") return null;
                const labels = {
                  amiante: "Amiante", plomb: "Plomb", accessibilite_pmr: "Accessibilité PMR",
                  securite_incendie: "Sécurité incendie", radon: "Radon",
                  electricite_conformite: "Conformité électrique", assainissement: "Assainissement",
                  autres_diagnostics: "Autres diagnostics"
                };
                return (
                  <div key={key} className="p-2 bg-slate-50 rounded-lg">
                    <p className="text-xs font-medium text-slate-500">{labels[key] || key}</p>
                    <p className="text-sm text-slate-700">{val}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Équipements détaillés (extraits du PDF) */}
        {equipements.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="equipements-section">
            <div className="p-5 border-b border-slate-100 bg-purple-50">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-purple-600" />
                Équipements installés ({equipements.length})
              </h3>
            </div>
            <div className="divide-y divide-slate-100">
              {equipements.map((eq, i) => (
                <div key={i} className="p-4 grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-3">
                    <p className="text-xs uppercase font-semibold text-purple-600">{eq.categorie}</p>
                    <p className="font-medium text-slate-900 text-sm">{eq.designation}</p>
                  </div>
                  <div className="col-span-3 text-xs text-slate-600">
                    {eq.marque && <p><span className="text-slate-400">Marque :</span> <span className="font-medium">{eq.marque}</span></p>}
                    {eq.modele && <p><span className="text-slate-400">Modèle :</span> {eq.modele}</p>}
                    {eq.localisation && <p><span className="text-slate-400">Lieu :</span> {eq.localisation}</p>}
                  </div>
                  <div className="col-span-3 text-xs text-slate-600">
                    {eq.annee_installation && <p><span className="text-slate-400">Installé en :</span> <span className="font-medium">{eq.annee_installation}</span></p>}
                    {eq.duree_vie_estimee_ans && <p><span className="text-slate-400">Durée de vie :</span> {eq.duree_vie_estimee_ans} ans</p>}
                    {eq.puissance_kw != null && <p><span className="text-slate-400">Puissance :</span> {eq.puissance_kw} kW</p>}
                    {eq.rendement_pct != null && <p><span className="text-slate-400">Rendement :</span> {eq.rendement_pct}%</p>}
                  </div>
                  <div className="col-span-3 text-xs">
                    {eq.etat && (
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${(ETAT_COLORS[eq.etat] || ETAT_COLORS['Non évalué']).bg} ${(ETAT_COLORS[eq.etat] || ETAT_COLORS['Non évalué']).text}`}>
                        {eq.etat}
                      </span>
                    )}
                    {eq.observations && <p className="text-slate-500 mt-1">{eq.observations}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Données chiffrées (températures, U-values, conso par poste, métrés) */}
        {(donneesChiffrees.temperatures_relevees?.length > 0 ||
          donneesChiffrees.u_values_parois?.length > 0 ||
          donneesChiffrees.metres_zones?.length > 0 ||
          (donneesChiffrees.consommations_par_poste &&
            Object.values(donneesChiffrees.consommations_par_poste).some(v => v != null)) ||
          (donneesChiffrees.surfaces_vitrees &&
            Object.values(donneesChiffrees.surfaces_vitrees).some(v => v != null && v !== ""))) && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="donnees-chiffrees-section">
            <div className="p-5 border-b border-slate-100 bg-cyan-50">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <ThermometerSun className="w-4 h-4 text-cyan-600" />
                Données chiffrées
              </h3>
            </div>
            <div className="p-5 space-y-5">
              {/* Températures */}
              {donneesChiffrees.temperatures_relevees?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Températures relevées</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {donneesChiffrees.temperatures_relevees.map((t, i) => (
                      <div key={i} className="p-2 bg-cyan-50 rounded border border-cyan-100 text-xs">
                        <p className="font-medium text-slate-700">{t.piece}</p>
                        <p className="text-slate-600">
                          {t.consigne_C != null && <>Consigne : <span className="font-semibold">{t.consigne_C}°C</span> · </>}
                          {t.mesuree_C != null && <>Mesurée : <span className="font-semibold">{t.mesuree_C}°C</span></>}
                          {t.humidite_pct != null && <> · HR {t.humidite_pct}%</>}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* U-values des parois */}
              {donneesChiffrees.u_values_parois?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Coefficients U des parois</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {donneesChiffrees.u_values_parois.map((u, i) => (
                      <div key={i} className="p-2 bg-amber-50 rounded border border-amber-100 text-xs">
                        <p className="font-medium text-slate-700">{u.paroi}</p>
                        <p className="text-slate-600">
                          {u.u_w_m2k != null && <>U = <span className="font-bold text-amber-700">{u.u_w_m2k} W/m².K</span></>}
                          {u.epaisseur_cm != null && <> · {u.epaisseur_cm} cm</>}
                        </p>
                        {u.composition && <p className="text-slate-500 italic mt-0.5">{u.composition}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Surfaces vitrées */}
              {donneesChiffrees.surfaces_vitrees && Object.values(donneesChiffrees.surfaces_vitrees).some(v => v != null && v !== "") && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Surfaces vitrées & menuiseries</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    {donneesChiffrees.surfaces_vitrees.totale_m2 != null && (
                      <div className="p-2 bg-blue-50 rounded"><p className="text-slate-500">Surface totale vitrée</p><p className="font-bold text-blue-700">{donneesChiffrees.surfaces_vitrees.totale_m2} m²</p></div>
                    )}
                    {donneesChiffrees.surfaces_vitrees.facade_principale_m2 != null && (
                      <div className="p-2 bg-blue-50 rounded"><p className="text-slate-500">Façade principale</p><p className="font-bold text-blue-700">{donneesChiffrees.surfaces_vitrees.facade_principale_m2} m²</p></div>
                    )}
                    {donneesChiffrees.surfaces_vitrees.type_vitrage && (
                      <div className="p-2 bg-blue-50 rounded"><p className="text-slate-500">Type vitrage</p><p className="font-medium text-slate-700">{donneesChiffrees.surfaces_vitrees.type_vitrage}</p></div>
                    )}
                    {donneesChiffrees.surfaces_vitrees.menuiseries_materiau && (
                      <div className="p-2 bg-blue-50 rounded"><p className="text-slate-500">Matériau</p><p className="font-medium text-slate-700">{donneesChiffrees.surfaces_vitrees.menuiseries_materiau}</p></div>
                    )}
                  </div>
                </div>
              )}
              {/* Consommations par poste */}
              {donneesChiffrees.consommations_par_poste && Object.values(donneesChiffrees.consommations_par_poste).some(v => v != null) && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Consommations par poste (kWh/an)</p>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                    {Object.entries({
                      chauffage_kwh: 'Chauffage', ecs_kwh: 'ECS', eclairage_kwh: 'Éclairage',
                      ventilation_kwh: 'Ventilation', auxiliaires_kwh: 'Auxiliaires'
                    }).map(([k, label]) => {
                      const v = donneesChiffrees.consommations_par_poste[k];
                      if (v == null) return null;
                      return (
                        <div key={k} className="p-2 bg-emerald-50 rounded text-center">
                          <p className="text-slate-500 text-[10px] uppercase">{label}</p>
                          <p className="font-bold text-emerald-700">{v.toLocaleString('fr-FR')}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Métrés par zone */}
              {donneesChiffrees.metres_zones?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Métrés par zone</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    {donneesChiffrees.metres_zones.map((z, i) => (
                      <div key={i} className="p-2 bg-slate-50 rounded">
                        <p className="font-medium text-slate-700">{z.zone}</p>
                        <p className="text-slate-600">
                          {z.longueur_m != null && <>L: {z.longueur_m}m · </>}
                          {z.largeur_m != null && <>l: {z.largeur_m}m · </>}
                          {z.hauteur_m != null && <>H: {z.hauteur_m}m</>}
                          {z.surface_m2 != null && <> · <span className="font-semibold">{z.surface_m2} m²</span></>}
                          {z.volume_m3 != null && <> · {z.volume_m3} m³</>}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Images extraites du PDF */}
        {imagesExtraites.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="pdf-images-section">
            <div className="p-5 border-b border-slate-100 bg-rose-50">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Eye className="w-4 h-4 text-rose-600" />
                Images extraites du PDF ({imagesExtraites.length})
              </h3>
            </div>
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              {imagesExtraites.map((img, i) => (
                <a key={i} href={img.url} target="_blank" rel="noopener noreferrer" className="block group">
                  <img src={img.url} alt={`Page ${img.page} #${img.index}`}
                    className="w-full h-32 object-cover rounded-lg border border-slate-200 group-hover:opacity-80 transition" />
                  <p className="text-[10px] text-slate-400 mt-1">Page {img.page} · Image {img.index}</p>
                </a>
              ))}
            </div>
          </div>
        )}

      </div>
    );
  }

  return null;
}
